// Job setup (docs/job-view-brief.md section 5; the drawing is docs/screens/job-view-screens-round4-settled-set.html, frame
// f2 and inset f2b). Answers: what does this whole job assume, who set it, and what does each build change?
// Two columns. Left: the job (type, design path, design lane, build type, linear feet, scope name), the job route
// (Standard beside This job, a column per build when "Different job routes per build" is on) and the Job BOM with the
// job size. Right: the company standards by job type (company default beside this job's value, gold where the job
// differs, cross-hatched where an AI set it and nobody has checked it, how many items a value reaches and how many hold
// their own), then the job standards as a grid by build (the base, then one column per other build, a build's cell its
// own value or grey, inherited), then the rooms, folded. A job standard is chosen on every job, so it carries no
// "changed" gold of its own (Spencer, 2026-10-15). A room follows the job's standards or holds its own values for some
// of them (Spencer, 2026-10-06). A jump from the Labor or Materials tab lands here with its row lit and a way back.

import { useEffect, useState } from 'react';
import { unavailableChoices, type LibStandardDef, type ResolvedBom, type ResolvedStep, type Scalar } from '@zerogap/engine';
import { deleteRow, insertRow, updateRow, upsertRow, useRows, type Row } from '../../lib/api';
import { addStartingItems } from '../../lib/create';
import { hours, minutes, money, plural, qty } from '../../lib/format';
import { baseBuild } from '../../lib/job';
import { appliesTo } from '../../lib/reads';
import { useSession } from '../../lib/session';
import { Btn, Edit, Empty, Info, Ln, useFolds } from '../../ui/kit';
import { priceWords } from '../../ui/pack';
import { Move } from '../../ui/move';
import { CatalogPick, MaterialPick, RecipePick, ValueEditor, showValue } from './ItemTab';
import type { JobCtx } from './Workspace';
import '../../styles/jobview-setup.css';

type Lib = NonNullable<JobCtx['r']['lib']>;

/** A date as the drawings write it beside a name: 10-17. */
function md(v: unknown): string {
  if (!v) return '';
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** An hours box as wide as its number, so the unit sits right after it. */
function fit(v: string): React.CSSProperties {
  return { width: `${Math.max(1.5, v.length + 0.6)}ch` };
}

/**
 * A Job setup section that folds (SM2, Spencer 2026-10-24): its heading carries a one-line summary read from what the
 * section holds ("12 steps · 17.5 h · 8 typed for this job"), who set it at the right; Job standards and Job route
 * start open, the rest closed, remembered for the session as the Labor and Materials tabs' folds are.
 */
function Grp({ title, summary, who, open, onToggle, children }: { title: string; summary: string; who?: React.ReactNode; open: boolean; onToggle: () => void; children?: React.ReactNode }) {
  return (
    <div className={`grp${open ? '' : ' folded'}`}>
      <h4>
        <button type="button" className="foldh" aria-expanded={open} onClick={onToggle}>
          <span className={`chev${open ? ' open' : ''}`} />
          {title}
        </button>
        <span className="ct">{summary}</span>
        {who}
      </h4>
      {open && children}
    </div>
  );
}

/** Which Job setup sections are open: Job standards and Job route start open, the others closed; both kept for the session. */
function useSetupFolds(jobId: string) {
  const closed = useFolds(`job-setup.${jobId}`, false);
  const opened = useFolds(`job-setup-open.${jobId}`, true);
  const of = (k: string) => (k === 'standards' || k === 'route' ? opened : closed);
  return { isOpen: (k: string) => of(k).isOpen(k), toggle: (k: string) => of(k).toggle(k), open: (k: string) => of(k).setAll([k], true) };
}

/** The newest of some rows by when they were last written. */
function newest(rows: Row[]): Row | undefined {
  return [...rows].sort((a, b) => String(b.updated_at ?? b.created_at ?? '').localeCompare(String(a.updated_at ?? a.created_at ?? '')))[0];
}

/** A standard's value as words: a material by its description, a catalog item by its name, else by its type. */
function words(lib: Lib, def: LibStandardDef, v: Scalar): string {
  if (def.data_type === 'material') return lib.materials[String(v ?? '')]?.description ?? '—';
  if (def.data_type === 'catalog_item') return lib.catalog[String(v ?? '')]?.name ?? '—';
  // inches as the editor shows them and the drawing writes them: 3 7/8, no inch mark
  return showValue(v, def.data_type, def.data_type === 'number' ? def.unit ?? null : null).replace(/"$/, '');
}

/** The editor a standard's type calls for: a material, a catalog item, a recipe name, or a value. */
function StandardEditor({ def, lib, value, standards, canEdit, onSet }: { def: LibStandardDef; lib: Lib; value: Scalar; standards: Record<string, Scalar>; canEdit: boolean; onSet: (v: Scalar) => void }) {
  const unit = def.data_type === 'number' ? def.unit ?? null : null;
  if (def.data_type === 'material') return canEdit ? <MaterialPick lib={lib} value={String(value ?? '')} filter={def.material_filter} onPick={(id) => onSet(id)} /> : <>{words(lib, def, value)}</>;
  if (def.data_type === 'catalog_item') return canEdit ? <CatalogPick lib={lib} value={String(value ?? '')} filter={def.material_filter} onPick={(id) => onSet(id)} /> : <>{words(lib, def, value)}</>;
  if (def.data_type === 'recipe') return canEdit ? <RecipePick lib={lib} value={String(value ?? '')} onPick={(name) => onSet(name)} /> : <>{String(value ?? '—')}</>;
  const out = def.data_type === 'choice' ? unavailableChoices(lib, standards, def) : {};
  return <ValueEditor type={def.data_type} unit={unit} choices={def.choices?.map((c) => c.value) ?? null} value={value} disabled={!canEdit} onSave={(v) => onSet(v)} unavailable={out} />;
}

/** What a standard is, for its i dot: what it applies to, the company default, its note, its choice's note. */
function StandardInfo({ def, row, lib, value }: { def: LibStandardDef; row: Row | undefined; lib: Lib; value: Scalar }) {
  const applies = appliesTo(lib, def.key);
  const choice = def.choices?.find((c) => c.value === value);
  return (
    <>
      {row?.info && <div style={{ marginBottom: 6 }}>{row.info}</div>}
      <div>
        <b>Applies to:</b> {applies.length ? applies.join(', ') : 'nothing in the catalog reads it yet'}
      </div>
      <div>
        <b>Company default:</b> {words(lib, def, def.default_value ?? null)}
      </div>
      {def.data_type === 'recipe' && <div style={{ marginTop: 6 }}>Names a recipe by the name the catalog items give it (Shop-built, Cabinotch); the picker offers every name in use and how many items carry it. Every catalog item that is picked by this standard resolves through its recipe of that name; an item with none is flagged on the job.</div>}
      {choice?.note && <div style={{ marginTop: 6 }}>{choice.note}</div>}
      {def.allow_item_override === false && <div style={{ marginTop: 6 }}>No item may hold its own value of this standard: every item follows this line.</div>}
    </>
  );
}

/** The warnings a standard's value carries where it is read: a choice ruled out here, a choice with no real materials. */
function standardWarnings(lib: Lib, def: LibStandardDef, value: Scalar, standards: Record<string, Scalar>): string[] {
  const out: string[] = [];
  const ruled = def.data_type === 'choice' ? unavailableChoices(lib, standards, def) : {};
  const now = value !== null && value !== '' ? ruled[String(value)] : undefined;
  if (now) out.push(`“${String(value)}” is not available here: ${now}. Pick another, or change what rules it out.`);
  const choice = def.choices?.find((c) => c.value === value);
  if (def.data_type === 'choice' && choice && !choice.materials && appliesTo(lib, def.key).length > 0 && def.choices?.some((c) => c.materials)) out.push('This choice does not name its real materials yet, so the lines it fills price at zero.');
  return out;
}

/**
 * One standard on a line, for a room's own standards: its value in force here (the room's own, or the job's), the i dot
 * with what it applies to and what the company default is, and the editor for its type.
 */
function StandardLine({
  def, row, lib, value, changed, changedWord, canEdit, onSet, extra, standards,
}: {
  def: LibStandardDef;
  row: Row | undefined;
  lib: Lib;
  value: Scalar;
  /** The standards in effect where this line is read (the room's own over the job's), for a choice's rule. */
  standards: Record<string, Scalar>;
  /** True when this line holds its own value (a room over the job). */
  changed: boolean;
  changedWord: string;
  canEdit: boolean;
  onSet: (value: Scalar) => void;
  /** More for the i dot: where the value above comes from. */
  extra?: React.ReactNode;
}) {
  const warns = standardWarnings(lib, def, value, standards);
  return (
    <Ln
      key={def.key}
      left={
        <>
          {def.label}
          <Info>
            <StandardInfo def={def} row={row} lib={lib} value={value} />
            {extra}
          </Info>
          {warns.map((w) => (
            <div key={w} className="warn">
              {w}
            </div>
          ))}
        </>
      }
      right={
        <span className={`nowrap${changed ? ' ov' : ''}`}>
          <StandardEditor def={def} lib={lib} value={value} standards={standards} canEdit={canEdit} onSet={onSet} />
          {changed && (
            <>
              {' '}
              <small>({changedWord})</small>{' '}
              {canEdit && (
                <Btn small title="Back to the job’s value" onClick={() => onSet(null)}>
                  Reset
                </Btn>
              )}
            </>
          )}
        </span>
      }
    />
  );
}

export function Setup({ ctx }: { ctx: JobCtx }) {
  const { data, r, jobId } = ctx;
  const s = useSession();
  const lib = r.lib!;
  const job = data.job!;
  // the job as entered: the values this screen edits (a build's own values are its column, never these)
  const entered = (ctx.asEntered.res ?? r.res)!;
  const types = useRows('job_type', { order: ['name'] });
  const pulls = useRows('job_type_standard', { eq: { job_type_id: job.job_type_id }, order: ['sort'], enabled: !!job.job_type_id });
  const defs = useRows('standard_def', { order: ['sort'] });
  const users = useRows('user');
  const canEdit = s.canEdit('screen.job_setup') && !ctx.locked;
  const ranges = s.setting<{ build_type: string }[]>('market_ranges', []);
  const paths = s.setting<{ key: string; label: string; hint?: string }[]>('design_paths', []);
  const answers = s.setting<{ design_lane?: { key: string; label: string }[] }>('pipeline_answers', {});
  const pooling = s.setting<{ scope: string }>('pooling', { scope: 'job' });
  const tools = s.setting<{ key: string; label: string }[]>('job_view_tools', []);
  const path = paths.find((p) => p.key === job.design_path);
  const who = (id: unknown) => users.rows.find((u) => u.id === id)?.name ?? '';
  const folds = useSetupFolds(jobId);

  const pulledIds = new Set(pulls.rows.map((p) => p.standard_def_id));
  const typed = !!job.job_type_id && pulls.rows.length > 0;
  const inType = (key: string) => {
    const row = defs.rows.find((d) => d.key === key);
    return !typed || (row ? pulledIds.has(row.id) : false);
  };
  // a catalog standard is an input pre-staged for catalog items, never a job's (Spencer, 2026-10-14)
  const jobKinds = lib.standards.filter((d) => d.kind !== 'catalog');
  const shown = jobKinds.filter((d) => inType(d.key));
  const hidden = jobKinds.filter((d) => !inType(d.key));
  // in the order the job type gives them (drag under Library → Job types), so what a job decides comes first
  const place = new Map(pulls.rows.map((p) => [p.standard_def_id as string, Number(p.sort) || 0]));
  const rank = (d: LibStandardDef) => {
    const row = defs.rows.find((x) => x.key === d.key);
    return row ? place.get(row.id) ?? 0 : 0;
  };
  shown.sort((a, b) => rank(a) - rank(b) || a.sort - b.sort);
  const overriding = (r.res?.items ?? []).filter((i) => i.overrides_count > 0);

  // a jump from the Labor or Materials tab: its section opens, and the lit row scrolls to the middle of the window
  const litKey = ctx.lit ? `${ctx.lit.kind}:${ctx.lit.key}` : '';
  useEffect(() => {
    if (!litKey || !ctx.lit) return;
    const section = ctx.lit.kind === 'route' ? 'route' : ctx.lit.kind === 'bom' ? 'bom' : lib.standards.find((d) => d.key === ctx.lit!.key)?.kind === 'company' ? 'company' : 'standards';
    folds.open(section);
    const t = window.setTimeout(() => document.querySelector('.jvs [data-lit-on]')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 120);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [litKey]);
  const tabName = (key: string) => tools.find((t) => t.key === key)?.label ?? ctx.steps.find((x) => x.tab === key)?.name ?? key;

  const designLane = data.opportunity?.design_lane as string | null | undefined;
  const laneWords = designLane ? answers.design_lane?.find((a) => a.key === designLane)?.label ?? designLane : 'Not set';

  return (
    <div className="jvs">
      {ctx.lit && ctx.back && (
        <div className="back">
          <button type="button" className="btn read" onClick={() => ctx.go({ tab: ctx.back, lit: null, back: null })}>
            ‹ Back to {tabName(ctx.back)}
          </button>
          <span className="where">{ctx.lit.kind === 'standard' ? 'The standard you jumped from is lit.' : ctx.lit.kind === 'bom' ? 'The Job BOM line you jumped from is lit.' : 'The job route step you jumped from is lit.'}</span>
        </div>
      )}
      <div className="jvs-cols">
        <div className="col">
          <Grp
            title="The job"
            summary={[types.rows.find((t) => t.id === job.job_type_id)?.name ?? 'no type', path?.label ?? 'no design path', job.linear_feet !== null && job.linear_feet !== undefined && job.linear_feet !== '' ? `${qty(job.linear_feet)} LF` : 'no linear feet'].join(' · ')}
            who={<span className="who">set at Scope{who(job.created_by) ? ` · ${who(job.created_by)}` : ''}</span>}
            open={folds.isOpen('job')}
            onToggle={() => folds.toggle('job')}
          >
            <table className="kvt kv-job">
              <colgroup>
                <col className="c-k" />
                <col />
                <col className="c-i" />
              </colgroup>
              <tbody>
                <tr>
                  <td className="k">Job type</td>
                  <td>
                    <select
                      className="in"
                      disabled={!canEdit}
                      value={job.job_type_id ?? ''}
                      onChange={(e) =>
                        void (async () => {
                          // a type picked later still gives the job the items the type starts every job with, skipping any it already has
                          await updateRow('job', jobId, { job_type_id: e.target.value || null });
                          if (e.target.value) await addStartingItems(jobId, e.target.value, data.items);
                        })()
                      }
                    >
                      <option value="">No type: show every standard</option>
                      {types.rows.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="i">
                    <Info left>The job type decides which standards pull in, and the job route and Job BOM every job of the type starts with (Library → Job types).</Info>
                  </td>
                </tr>
                <tr>
                  <td className="k">Design path</td>
                  <td>
                    <select className="in" disabled={!s.can('screen.job_setup')} value={job.design_path ?? ''} onChange={(e) => void updateRow('job', jobId, { design_path: e.target.value || null })}>
                      <option value="">Not chosen yet</option>
                      {paths.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    {!job.design_path && paths.length > 0 && <div className="warn">Not chosen yet, so any step that depends on it is left out of this job.</div>}
                  </td>
                  <td className="i">
                    <Info left>
                      The design path decides which workflow steps this job has.
                      {path?.hint && <div style={{ marginTop: 6 }}>{path.hint}</div>}
                    </Info>
                  </td>
                </tr>
                <tr>
                  <td className="k">Design lane</td>
                  <td className="ro">{laneWords} · from the opportunity</td>
                  <td className="i">
                    <Info left>The lane is the lead's, set on the opportunity in the pipeline. In the design-rounds lane the job has a Quick check before the Engineering check.</Info>
                  </td>
                </tr>
                <tr>
                  <td className="k">Build type · market check</td>
                  <td>
                    <select className="in" disabled={!canEdit} value={job.build_type ?? ''} onChange={(e) => void updateRow('job', jobId, { build_type: e.target.value || null })}>
                      <option value="">Not set</option>
                      {ranges.map((x) => (
                        <option key={x.build_type} value={x.build_type}>
                          {x.build_type}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="i">
                    <Info left>The build type the price per foot is checked against (Settings → Estimating → Market ranges).</Info>
                  </td>
                </tr>
                <tr>
                  <td className="k">Linear feet</td>
                  <td>
                    <Edit className="in" disabled={!canEdit} type="number" value={job.linear_feet ?? ''} onSave={(v) => updateRow('job', jobId, { linear_feet: v === '' ? null : Number(v) })} />
                  </td>
                  <td className="i">
                    <Info left>The job's linear feet: the price per foot and the job size read it.</Info>
                  </td>
                </tr>
                <tr>
                  <td className="k">Scope name</td>
                  <td>
                    <Edit className="in" disabled={!canEdit} value={job.scope_name} onSave={(v) => updateRow('job', jobId, { scope_name: v })} />
                  </td>
                  <td className="i">
                    <Info left>What this job is called on the opportunity, the Jobs list and the proposal.</Info>
                  </td>
                </tr>
              </tbody>
            </table>
          </Grp>

          <JobRoute ctx={ctx} canEdit={canEdit} who={who} folds={folds} />
        </div>

        <div className="col">
          <CompanyStandards ctx={ctx} defs={defs.rows} shown={shown.filter((d) => d.kind === 'company')} canEdit={canEdit} who={who} entered={entered.standards} typed={typed} open={folds.isOpen('company')} onToggle={() => folds.toggle('company')} />
          <JobStandards ctx={ctx} defs={defs.rows} shown={shown.filter((d) => d.kind === 'job')} canEdit={canEdit} who={who} entered={entered.standards} open={folds.isOpen('standards')} onToggle={() => folds.toggle('standards')} />
          <Rooms ctx={ctx} shown={shown} defs={defs.rows} canEdit={canEdit} pooling={pooling.scope} open={folds.isOpen('rooms')} onToggle={() => folds.toggle('rooms')} onOpen={() => folds.open('rooms')} />
          <small className="only">
            {hidden.length > 0 && <>Not pulled in for this type: {hidden.map((d) => d.label.toLowerCase()).join(', ')} · </>}
            {overriding.length === 0 ? (
              '0 items override a standard'
            ) : (
              <button type="button" className="reach" title={overriding.map((i) => i.number).join(', ')} onClick={() => ctx.go({ tab: 'item', item: overriding[0]!.id, lit: null, back: null })}>
                {overriding.length} {overriding.length === 1 ? 'item overrides' : 'items override'} a standard: {overriding.map((i) => i.number).join(', ')}
              </button>
            )}
          </small>
        </div>
      </div>
    </div>
  );
}

/** True when a row of the job's own setup was written since an AI employee last changed it and nobody verified it. */
function aiSet(job: Row, row: Row | undefined): boolean {
  if (!row || !job.unverified_since) return false;
  return String(row.updated_at ?? row.created_at ?? '') >= String(job.unverified_since);
}

// ---------- company standards ----------

/**
 * The company standards the job type pulls in: the company's default grey, this job's value (gold where the job holds
 * its own), cross-hatched where an AI set it and nobody has checked it; under the value, how many items a changed value
 * reaches and how many items hold their own instead, both read from the standards each item reads.
 */
function CompanyStandards({ ctx, defs, shown, canEdit, who, entered, typed, open: isOpen, onToggle }: { ctx: JobCtx; defs: Row[]; shown: LibStandardDef[]; canEdit: boolean; who: (id: unknown) => string; entered: Record<string, Scalar>; typed: boolean; open: boolean; onToggle: () => void }) {
  const { data, r, jobId } = ctx;
  const lib = r.lib!;
  const job = data.job!;
  const items = r.res?.items ?? [];
  const rowOf = (def: LibStandardDef) => {
    const row = defs.find((d) => d.key === def.key);
    return { row, jobRow: row ? data.jobStandards.find((j) => j.standard_def_id === row.id) : undefined };
  };
  const changed = shown.filter((d) => !!rowOf(d).jobRow);
  const set = async (def: LibStandardDef, value: Scalar) => {
    const { row, jobRow } = rowOf(def);
    if (!row) return;
    const same = value === null || value === '' || String(value) === String(def.default_value ?? '');
    if (same) {
      if (jobRow) await deleteRow('job_standard', jobRow.id);
    } else {
      await upsertRow('job_standard', { job_id: jobId, standard_def_id: row.id, value: String(value), changed_from_default: true }, 'job_id,standard_def_id');
    }
  };
  const lastBy = newest(changed.map((d) => rowOf(d).jobRow!).filter(Boolean));
  const open = (ids: string[]) => ids[0] && ctx.go({ tab: 'item', item: ids[0], lit: null, back: null });

  return (
    <Grp
      title="Company standards, by job type"
      summary={`${shown.length} · ${changed.length} changed on this job`}
      who={<span className="who">{lastBy ? `changed here: ${who(lastBy.created_by) || 'someone'} ${md(lastBy.updated_at)}` : typed ? 'pulled in by type' : 'every standard'}</span>}
      open={isOpen}
      onToggle={onToggle}
    >
      {shown.length === 0 ? (
        <Empty>None for this job type.</Empty>
      ) : (
        <div className="scr">
          <table className="kvt kv-co">
            <colgroup>
              <col className="c-k" />
              <col className="c-std" />
              <col />
              <col className="c-i" />
            </colgroup>
            <thead>
              <tr>
                <th>Standard</th>
                <th>Company</th>
                <th>This job</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((def) => {
                const { row, jobRow } = rowOf(def);
                const value = entered[def.key] ?? null;
                const hatched = aiSet(job, jobRow);
                const readers = items.filter((i) => i.standards_used.some((u) => u.key === def.key));
                const holders = readers.filter((i) => i.standards_used.some((u) => u.key === def.key && u.overridden));
                // the items holding their own value, by the value they hold
                const held = new Map<string, string[]>();
                for (const i of holders) {
                  const u = i.standards_used.find((x) => x.key === def.key)!;
                  const w = words(lib, def, u.value);
                  held.set(w, [...(held.get(w) ?? []), i.id]);
                }
                const reach = readers.filter((i) => !holders.includes(i));
                const warns = standardWarnings(lib, def, value, entered);
                const lit = ctx.lit?.kind === 'standard' && ctx.lit.key === def.key;
                return [
                  <tr key={def.key} className={lit ? 'lit' : undefined} data-lit-on={lit ? '' : undefined}>
                    <td className="k">{def.label}</td>
                    <td className="cd">{words(lib, def, def.default_value ?? null)}</td>
                    <td>
                      <div className="cellv">
                        <span className={`ed${jobRow ? ' ch' : ''}${hatched ? ' hatch' : ''}`}>
                          <StandardEditor def={def} lib={lib} value={value} standards={entered} canEdit={canEdit} onSet={(v) => void set(def, v)} />
                        </span>
                        {jobRow && (
                          <button type="button" className="reach" title={reach.map((i) => i.number).join(', ')} onClick={() => open(reach.map((i) => i.id))}>
                            reaches {reach.length} {reach.length === 1 ? 'item' : 'items'}
                            {ctx.build && data.builds.length > 1 ? ` on ${ctx.build.name}` : ''}
                          </button>
                        )}
                        {[...held.entries()].map(([w, ids]) => (
                          <button key={w} type="button" className="reach" title={ids.map((id) => items.find((i) => i.id === id)?.number).join(', ')} onClick={() => open(ids)}>
                            {ids.length} {ids.length === 1 ? 'item holds' : 'items hold'} {w}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="i">
                      <Info left>
                        <StandardInfo def={def} row={row} lib={lib} value={value} />
                        {hatched && (
                          <div style={{ marginTop: 6 }}>
                            <b>{who(job.unverified_by) || 'An AI employee'}</b> · {md(job.unverified_since)} · unverified until your Engineering check
                          </div>
                        )}
                        {jobRow && canEdit && (
                          <div style={{ marginTop: 8 }}>
                            <Btn small onClick={() => void set(def, null)}>
                              Back to the company default
                            </Btn>
                          </div>
                        )}
                      </Info>
                    </td>
                  </tr>,
                  warns.length > 0 && (
                    <tr key={`${def.key}:w`} className="wn">
                      <td colSpan={4}>
                        {warns.map((w) => (
                          <div key={w} className="warn">
                            {w}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </Grp>
  );
}

// ---------- job standards, by build ----------

/**
 * The job standards as a grid: one row per standard, the base first, then one column per other build from the Scope
 * table (Spencer, 2026-10-22: "much more clear what has changed when both things are sitting next to each other"). The
 * base column writes the job's own value (a job_standard row), or the base build's own value where it holds one; a
 * build's column writes that build's override (build.global_overrides) or reads grey, inherited from the base.
 */
function JobStandards({ ctx, defs, shown, canEdit, who, entered, open, onToggle }: { ctx: JobCtx; defs: Row[]; shown: LibStandardDef[]; canEdit: boolean; who: (id: unknown) => string; entered: Record<string, Scalar>; open: boolean; onToggle: () => void }) {
  const { data, r, jobId } = ctx;
  const lib = r.lib!;
  const job = data.job!;
  const base = baseBuild(data.builds);
  const others = data.builds.filter((b) => b.id !== base?.id);
  const baseOv = (base?.global_overrides ?? {}) as Record<string, Scalar>;
  const [editing, setEditing] = useState<string | null>(null);
  const rowOf = (def: LibStandardDef) => {
    const row = defs.find((d) => d.key === def.key);
    return { row, jobRow: row ? data.jobStandards.find((j) => j.standard_def_id === row.id) : undefined };
  };
  const setJob = async (def: LibStandardDef, value: Scalar) => {
    const { row, jobRow } = rowOf(def);
    if (!row) return;
    // a job standard is picked for every job: a value the same as the company's default is still kept as this job's pick
    if (value === null || value === '') {
      if (jobRow) await deleteRow('job_standard', jobRow.id);
    } else {
      await upsertRow('job_standard', { job_id: jobId, standard_def_id: row.id, value: String(value), changed_from_default: true }, 'job_id,standard_def_id');
    }
  };
  const setBuild = async (build: Row, key: string, value: Scalar | undefined) => {
    const o = { ...((build.global_overrides ?? {}) as Record<string, Scalar>) };
    if (value === undefined || value === null || value === '') delete o[key];
    else o[key] = value;
    await updateRow('build', build.id, { global_overrides: o });
  };
  const baseValue = (key: string): Scalar => (key in baseOv ? baseOv[key]! : entered[key] ?? null);
  const baseStandards: Record<string, Scalar> = { ...entered, ...baseOv };
  const jobRows = shown.map((d) => rowOf(d).jobRow).filter(Boolean) as Row[];
  const lastJob = newest(jobRows);
  const whoLine = [
    lastJob ? `${who(lastJob.created_by) || 'set'} ${md(lastJob.updated_at)}` : null,
    ...others.filter((b) => Object.keys(b.global_overrides ?? {}).length > 0).map((b) => `${b.name} overrides: ${who(b.created_by) || 'set'} ${md(b.updated_at)}`),
  ].filter(Boolean);
  const hatchedCount = shown.filter((d) => aiSet(job, rowOf(d).jobRow)).length;
  const overrideCount = others.reduce((n, b) => n + Object.keys((b.global_overrides ?? {}) as object).length, 0) + Object.keys(baseOv).length;
  const summary = [`${shown.length}`, overrideCount ? `${overrideCount} build ${overrideCount === 1 ? 'override' : 'overrides'}` : null, hatchedCount ? `${hatchedCount} unverified` : null].filter(Boolean).join(' · ');

  return (
    <Grp title="Job standards, this job" summary={summary} who={<span className="who">{whoLine.join(' · ')}</span>} open={open} onToggle={onToggle}>
      {shown.length === 0 ? (
        <Empty>None for this job type.</Empty>
      ) : (
        <div className="scr">
          <table className="kvt kv-jsg" style={{ minWidth: 128 + 200 * (1 + others.length) }}>
            <colgroup>
              <col className="c-k2" />
              <col />
              {others.map((b) => (
                <col key={b.id} />
              ))}
              <col className="c-i" />
            </colgroup>
            <thead>
              <tr>
                <th />
                <th className="b">{base ? `Base · ${base.name}` : 'This job'}</th>
                {others.map((b) => (
                  <th key={b.id} className="b">
                    {b.name}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((def) => {
                const { row, jobRow } = rowOf(def);
                const onBase = def.key in baseOv;
                // the base build's own value is gold only where it differs from the job's own (the edit still goes to it, so it is never shadowed)
                const baseDiffers = onBase && String(baseOv[def.key] ?? '') !== String(entered[def.key] ?? '');
                const value = baseValue(def.key);
                const hatched = !onBase && aiSet(job, jobRow);
                const lit = ctx.lit?.kind === 'standard' && ctx.lit.key === def.key;
                const warns = standardWarnings(lib, def, value, baseStandards);
                return [
                  <tr key={def.key} className={lit ? 'lit' : undefined} data-lit-on={lit ? '' : undefined}>
                    <td className="k">{def.label}</td>
                    <td>
                      <div className="cellv">
                        <span className={`ed${baseDiffers ? ' ch' : ''}${hatched ? ' hatch' : ''}`}>
                          <StandardEditor def={def} lib={lib} value={value} standards={baseStandards} canEdit={canEdit} onSet={(v) => void (onBase && base ? setBuild(base, def.key, v) : setJob(def, v))} />
                        </span>
                        {baseDiffers && base && canEdit && (
                          <button type="button" className="x" title={`Back to the job's own value (${words(lib, def, entered[def.key] ?? null)})`} onClick={() => void setBuild(base, def.key, undefined)}>
                            ×
                          </button>
                        )}
                      </div>
                    </td>
                    {others.map((b) => {
                      const ov = (b.global_overrides ?? {}) as Record<string, Scalar>;
                      const has = def.key in ov;
                      const cell = `${b.id}:${def.key}`;
                      const standards = { ...baseStandards, ...ov };
                      if (has || editing === cell)
                        return (
                          <td key={b.id}>
                            <div className="cellv">
                              <span className={`ed${has ? ' ch' : ''}`}>
                                <StandardEditor
                                  def={def}
                                  lib={lib}
                                  value={has ? ov[def.key]! : value}
                                  standards={standards}
                                  canEdit={canEdit}
                                  onSet={(v) => {
                                    setEditing(null);
                                    // the base's own value typed in a build's cell is no override: the cell reads inherited again
                                    void setBuild(b, def.key, v !== null && String(v) === String(value ?? '') ? undefined : v);
                                  }}
                                />
                              </span>
                              {canEdit && (
                                <button type="button" className="x" title={has ? `Back to inherited (${words(lib, def, value)})` : 'Keep inherited'} onClick={() => (has ? void setBuild(b, def.key, undefined) : setEditing(null))}>
                                  ×
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      // grey, with no "inherited ·" before it (F5): the colour carries it, the hover says so
                      return (
                        <td key={b.id} className="inh">
                          {canEdit ? (
                            <button type="button" className="inh" title={`Inherited: ${b.name} reads the base's value. Click to give it its own.`} onClick={() => setEditing(cell)}>
                              {words(lib, def, value)}
                            </button>
                          ) : (
                            <span title="Inherited from the base">{words(lib, def, value)}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="i">
                      <Info left>
                        <StandardInfo def={def} row={row} lib={lib} value={value} />
                        {baseDiffers && base && (
                          <div style={{ marginTop: 6 }}>
                            The base build {base.name} holds its own value here; the job's own is {words(lib, def, entered[def.key] ?? null)}.
                          </div>
                        )}
                        {hatched && (
                          <div style={{ marginTop: 6 }}>
                            <b>{who(job.unverified_by) || 'An AI employee'}</b> · {md(job.unverified_since)} · unverified until your Engineering check
                          </div>
                        )}
                      </Info>
                    </td>
                  </tr>,
                  warns.length > 0 && (
                    <tr key={`${def.key}:w`} className="wn">
                      <td colSpan={3 + others.length}>
                        {warns.map((w) => (
                          <div key={w} className="warn">
                            {w}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </Grp>
  );
}

// ---------- the job route and the Job BOM ----------

/**
 * The job route (Spencer, 2026-10-08): the job-level steps of the job's type (site measurements, design, engineering, the
 * install extras), each with the hours typed for this job beside the type's standard (Spencer, 2026-10-12: what the job
 * type starts every job with). A changed hour is gold, a blank one says "to fix". With "Different job routes per build"
 * on, every other build gets a column: its own hours (build.route_overrides, by the job route line's id, or
 * type:<type step id> where the job typed none) or grey, inherited. Under it the Job BOM: the job-wide lines, not on an
 * item, and the job size the small/large-job line reads (the job_size_bands setting against the job's linear feet).
 */
function JobRoute({ ctx, canEdit, who, folds }: { ctx: JobCtx; canEdit: boolean; who: (id: unknown) => string; folds: ReturnType<typeof useSetupFolds> }) {
  const { data, r, jobId } = ctx;
  const s = useSession();
  const lib = r.lib!;
  const job = data.job!;
  const res = (ctx.asEntered.res ?? r.res)!;
  const group = res.groups.find((g) => g.kind === 'job');
  const ops = useRows('operation', { order: ['sort', 'name'] });
  const mats = useRows('material', { order: ['description'] });
  const [editing, setEditing] = useState<string | null>(null);
  const typeSteps = data.typeSteps;
  const typeLines = data.typeBomLines;
  const ownStep = (st: Row) => data.routeLines.find((l) => l.type_step_id === st.id);
  const ownLine = (t: Row) => data.bomLines.find((l) => l.type_line_id === t.id);
  const addedSteps = data.routeLines.filter((l) => !l.type_step_id || !typeSteps.some((st) => st.id === l.type_step_id));
  const addedLines = data.bomLines.filter((l) => !l.type_line_id || !typeLines.some((t) => t.id === l.type_line_id));
  const stepOf = (lineId: string): ResolvedStep | undefined => group?.steps.find((st) => st.step_id === lineId);
  const lineOf = (lineId: string): ResolvedBom | undefined => group?.bom.find((b) => b.line_id === lineId);
  const phaseOf = (opId: string) => {
    const p = lib.operations[opId]?.phase_id;
    return p ? lib.phases[p]?.name : undefined;
  };
  const number = (t: string) => (t.trim() === '' ? '' : /^\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined);
  const live = ops.rows.filter((o) => !o.retired);
  const phases = Object.values(lib.phases).sort((a, b) => a.sort - b.sort);
  const opGroups = [
    ...phases.map((p) => ({ key: p.id, label: p.name, ops: live.filter((o) => o.phase_id === p.id) })),
    { key: 'none', label: 'No phase', ops: live.filter((o) => !o.phase_id || !lib.phases[o.phase_id]) },
  ].filter((g) => g.ops.length > 0);
  const count = typeSteps.length + addedSteps.length;

  const perBuild = !!job.routes_per_build;
  const base = baseBuild(data.builds);
  const others = perBuild ? data.builds.filter((b) => b.id !== base?.id) : [];
  const lastRoute = newest(data.routeLines);
  const routeBy = lastRoute ? `${who(lastRoute.created_by) || 'typed'} ${md(lastRoute.updated_at)}` : '';

  const typeStepQty = async (st: Row, v: string) => {
    const have = ownStep(st);
    if (have) await updateRow('job_route_line', have.id, { qty: v === '' ? null : v });
    // a blank typed over the quantity the type starts with is this job saying none, so it is kept as a line too
    else if (v !== '' || (st.qty !== null && st.qty !== undefined)) await insertRow('job_route_line', { job_id: jobId, type_step_id: st.id, operation_id: st.operation_id, qty: v === '' ? null : v, sort: 0 });
  };
  const typeLineQty = async (t: Row, v: string) => {
    const have = ownLine(t);
    if (have) await updateRow('job_bom_line', have.id, { qty: v === '' ? null : v });
    else await insertRow('job_bom_line', { job_id: jobId, type_line_id: t.id, material_id: t.material_id, qty: v === '' ? null : v, sort: 0 });
  };
  /** A build's own hours on one route line, under either name the line has had (its own line's id, or type:<type step id>). */
  const buildHours = (b: Row, keys: string[]): string | undefined => {
    const o = (b.route_overrides ?? {}) as Record<string, unknown>;
    for (const k of keys) if (o[k] !== undefined && o[k] !== null && o[k] !== '') return String(o[k]);
    return undefined;
  };
  const setBuildHours = async (b: Row, keys: string[], v: string | undefined) => {
    const o = { ...((b.route_overrides ?? {}) as Record<string, unknown>) };
    for (const k of keys) delete o[k];
    if (v !== undefined && v !== '') o[keys[0]!] = v;
    await updateRow('build', b.id, { route_overrides: o });
  };
  const unitShort = (opId: string) => {
    const op = lib.operations[opId];
    const w = op?.unit ? plural(op.unit) : '';
    return w === 'hours' ? 'h' : w === 'minutes' ? 'min' : w;
  };

  /**
   * One route step. `keys` are the names a build's override may sit under (the first is where a new one is written);
   * `litKeys` the names a jump may light it by.
   */
  const stepRow = (
    key: string,
    keys: string[],
    litKeys: string[],
    opId: string,
    note: string | null,
    value: string,
    onQty: (v: string) => Promise<unknown>,
    resolved: ResolvedStep | undefined,
    lineRow: Row | undefined,
    standard: unknown,
    added?: Row,
  ) => {
    const op = lib.operations[opId];
    const phase = phaseOf(opId);
    const u = unitShort(opId);
    const unitWord = op?.unit ? plural(op.unit) : '';
    const typedAsTime = (unitWord === 'hours' && Number(op?.minutes_per_unit) === 60) || (unitWord === 'minutes' && Number(op?.minutes_per_unit) === 1);
    const minutesEach = op?.minutes_per_unit ? (typedAsTime ? '' : `${qty(op.minutes_per_unit)} min each`) : 'no minutes set yet';
    const hasStandard = standard !== null && standard !== undefined && String(standard) !== '';
    const off = hasStandard ? Math.round((Number(value === '' ? 0 : value) - Number(standard)) * 10000) / 10000 : 0;
    const changed = added ? true : hasStandard && (off !== 0 || value === '');
    const blank = value === '';
    const hatched = aiSet(job, lineRow);
    const opNote = hasStandard ? null : ((ops.rows.find((o) => o.id === opId)?.notes as string | null | undefined) ?? null);
    const lit = ctx.lit?.kind === 'route' && litKeys.includes(ctx.lit.key);
    const warns = resolved?.warnings.filter((w) => !w.startsWith('Nothing typed')) ?? [];
    return [
      <tr key={key} className={lit ? 'lit' : undefined} data-lit-on={lit ? '' : undefined}>
        <td className="k">
          {op?.name ?? 'An operation no longer in the library'}
          {added && <small className="add"> · this job's own</small>}
        </td>
        <td className="cd">{hasStandard ? `${qty(String(standard))} ${u}` : '—'}</td>
        <td>
          <div className="cellv">
            <span className={`in hr${changed && !blank ? ' ch' : ''}${hatched ? ' hatch' : ''}`}>
              <Edit className="num" style={fit(value)} disabled={!canEdit} value={value} placeholder="—" parse={number} onSave={onQty} />
              <span className="u">{u}</span>
              {blank && <span className="tag fix">to fix</span>}
            </span>
            {added && canEdit && (
              <button type="button" className="x" title="Remove this step from the job's route" onClick={() => window.confirm(`Remove "${op?.name ?? 'this step'}" from this job's route?`) && void deleteRow('job_route_line', added.id)}>
                ×
              </button>
            )}
          </div>
        </td>
        {others.map((b) => {
          const own = buildHours(b, keys);
          const cell = `${b.id}:${key}`;
          if (own !== undefined || editing === cell)
            return (
              <td key={b.id}>
                <div className="cellv">
                  <span className={`in hr${own !== undefined ? ' ch' : ''}`}>
                    <Edit
                      className="num"
                      style={fit(own ?? value)}
                      disabled={!canEdit}
                      value={own ?? value}
                      placeholder="—"
                      parse={number}
                      onSave={(v) => {
                        setEditing(null);
                        // the base's hours typed in a build's cell are no override: it reads inherited again
                        return setBuildHours(b, keys, v === value ? undefined : v);
                      }}
                    />
                    <span className="u">{u}</span>
                  </span>
                  {canEdit && (
                    <button type="button" className="x" title={own !== undefined ? `Back to inherited (${value || '—'} ${u})` : 'Keep inherited'} onClick={() => (own !== undefined ? void setBuildHours(b, keys, undefined) : setEditing(null))}>
                      ×
                    </button>
                  )}
                </div>
              </td>
            );
          return (
            <td key={b.id} className="inh">
              {canEdit ? (
                <button type="button" className="inh" title={`Inherited: ${b.name} takes the base's hours. Click to give it its own.`} onClick={() => setEditing(cell)}>
                  {value === '' ? '—' : `${qty(value)} ${u}`}
                </button>
              ) : (
                <span title="Inherited from the base">{value === '' ? '—' : `${qty(value)} ${u}`}</span>
              )}
            </td>
          );
        })}
        <td className="i">
          <Info left>
            <div>
              <b>{op?.name ?? 'An operation no longer in the library'}</b>
              {phase ? ` · ${phase}` : ''}
            </div>
            {note && <div style={{ marginTop: 4 }}>{note}</div>}
            <div style={{ marginTop: 6 }}>
              <b>Standard:</b> {hasStandard ? `${qty(String(standard))} ${unitWord || 'units'}, what this job type starts every job with (Library → Job types)` : 'none: the type starts this step blank'}
              {hasStandard && off !== 0 && <> · this job is {value === '' ? 'none' : `${qty(Math.abs(off))} ${off > 0 ? 'over' : 'under'}`}</>}
            </div>
            {minutesEach && <div>{minutesEach} (Library → Operations)</div>}
            <div>
              <b>Priced:</b> {resolved && Number(resolved.minutes) > 0 ? minutes(resolved.minutes) : 'nothing'} on this job
            </div>
            {opNote && <div style={{ marginTop: 6 }}>{opNote}</div>}
            {hatched && (
              <div style={{ marginTop: 6 }}>
                <b>{who(job.unverified_by) || 'An AI employee'}</b> · {md(job.unverified_since)} · unverified until your Engineering check
              </div>
            )}
          </Info>
        </td>
      </tr>,
      warns.length > 0 && (
        <tr key={`${key}:w`} className="wn">
          <td colSpan={4 + others.length}>
            {warns.map((w) => (
              <div key={w} className="warn">
                {w}
              </div>
            ))}
          </td>
        </tr>
      ),
    ];
  };

  const bomRow = (key: string, litKeys: string[], materialId: string, note: string | null, value: string, onQty: (v: string) => Promise<unknown>, resolved: ResolvedBom | undefined, lineRowRow: Row | undefined, standard: unknown, added?: Row) => {
    const m = lib.materials[materialId];
    const hasStandard = standard !== null && standard !== undefined && String(standard) !== '';
    const changed = added ? true : hasStandard ? Number(value === '' ? 0 : value) !== Number(standard) : value !== '';
    const hatched = aiSet(job, lineRowRow);
    const lit = ctx.lit?.kind === 'bom' && litKeys.includes(ctx.lit.key);
    const unit = m?.unit ?? 'each';
    return [
      <tr key={key} className={lit ? 'lit' : undefined} data-lit-on={lit ? '' : undefined}>
        <td className="k">
          {m?.description ?? 'A material no longer in the library'}
          {added && <small className="add"> · this job's own</small>}
        </td>
        <td className="cd">{hasStandard ? `${qty(String(standard))} ${unit}` : '—'}</td>
        <td>
          <div className="cellv">
            <span className={`in hr${changed ? ' ch' : ''}${hatched ? ' hatch' : ''}`}>
              <Edit className="num" style={fit(value)} disabled={!canEdit} value={value} placeholder="—" parse={number} onSave={onQty} />
              <span className="u">{unit}</span>
            </span>
            {added && canEdit && (
              <button type="button" className="x" title="Remove this line from the Job BOM" onClick={() => window.confirm(`Remove "${m?.description ?? 'this line'}" from this job's BOM?`) && void deleteRow('job_bom_line', added.id)}>
                ×
              </button>
            )}
          </div>
        </td>
        <td className="i">
          <Info left>
            <div>
              <b>{m?.description ?? 'A material no longer in the library'}</b> · {m ? priceWords(m) : 'no price'}
            </div>
            {note && <div style={{ marginTop: 4 }}>{note}</div>}
            <div style={{ marginTop: 6 }}>
              <b>Standard:</b> {hasStandard ? `${qty(String(standard))} ${unit}, what this job type starts every job with` : 'none'}
            </div>
            <div>
              <b>Priced:</b> {resolved && resolved.cents > 0 ? `$${(resolved.cents / 100).toFixed(2)}` : 'nothing'} on this job
            </div>
          </Info>
        </td>
      </tr>,
      (resolved?.warnings.length ?? 0) > 0 && (
        <tr key={`${key}:w`} className="wn">
          <td colSpan={4}>
            {resolved!.warnings.map((w) => (
              <div key={w} className="warn">
                {w}
              </div>
            ))}
          </td>
        </tr>
      ),
    ];
  };

  // the job size: small up to the band's linear feet (the job_size_lf setting), read against this job's linear feet
  const bands = s.setting<unknown>('job_size_lf', null);
  const smallUpTo = bands && typeof bands === 'object' && !Array.isArray(bands) && (bands as Record<string, unknown>).small_up_to_lf !== undefined ? Number((bands as Record<string, unknown>).small_up_to_lf) : null;
  const lf = job.linear_feet === null || job.linear_feet === undefined || job.linear_feet === '' ? null : Number(job.linear_feet);
  const sizeWords = lf === null ? 'linear feet not set' : smallUpTo === null || !Number.isFinite(smallUpTo) ? `${qty(lf)} LF` : `${lf <= smallUpTo ? 'small' : 'large'} · ${qty(lf)} LF`;

  const typedCount = data.routeLines.filter((l) => l.qty !== null && l.qty !== undefined && String(l.qty) !== '').length;
  const bomCents = group?.bom.reduce((n, b) => n + b.cents, 0) ?? 0;
  const bomCount = typeLines.length + addedLines.length;
  return (
    <>
      <Grp
        title="Job route"
        summary={`${count} ${count === 1 ? 'step' : 'steps'} · ${group && Number(group.labor_minutes) > 0 ? hours(group.labor_minutes) : '0 h'} · ${typedCount} typed for this job`}
        who={<span className="who">typed hours{routeBy ? ` · ${routeBy}` : ''}</span>}
        open={folds.isOpen('route')}
        onToggle={() => folds.toggle('route')}
      >
        <div className="swrow">
          <button type="button" className={`sw${perBuild ? ' on' : ''}`} role="switch" aria-checked={perBuild} disabled={!canEdit} onClick={async () => {
              // off: one route for the whole job; a build's own hours go with the switch, so no second home is left behind
              const held = ctx.data.builds.filter((b) => Object.keys((b.route_overrides ?? {}) as object).length > 0);
              if (perBuild && held.length > 0 && !window.confirm(`Turn off different routes per build? The hours typed for ${held.map((b) => b.name).join(', ')} go, and every build prices from this job's route.`)) return;
              if (perBuild) for (const b of held) await updateRow('build', b.id, { route_overrides: {} });
              await updateRow('job', jobId, { routes_per_build: !perBuild });
            }}>
            <i />
            <span>Different job routes per build</span>
          </button>
          <span className="only">{perBuild ? (others.length > 0 ? 'on: a column per build from the Scope table' : 'on: no other build on the Scope table yet') : 'off: one route for the whole job'}</span>
        </div>
        {count === 0 ? (
          <Empty>{job.job_type_id ? 'This job type has no job route yet: set one up under Library → Job types, or add a step below.' : 'Pick a job type above to get its job route, or add a step below.'}</Empty>
        ) : (
          <div className="scr">
            <table className={`kvt kv-rt${others.length ? ' per' : ''}`} style={others.length ? { minWidth: 140 + 72 + 130 * (1 + others.length) + 24 } : undefined}>
              <colgroup>
                <col className="c-k" />
                <col className="c-std" />
                <col />
                {others.map((b) => (
                  <col key={b.id} />
                ))}
                <col className="c-i" />
              </colgroup>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Standard</th>
                  <th className={others.length ? 'b' : undefined}>{others.length ? `Base · ${base?.name ?? 'the job'}` : 'This job'}</th>
                  {others.map((b) => (
                    <th key={b.id} className="b">
                      {b.name}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {typeSteps.map((st) => {
                  const own = ownStep(st);
                  const value = own ? (own.qty === null || own.qty === undefined ? '' : String(own.qty)) : st.qty === null || st.qty === undefined ? '' : String(st.qty);
                  const keys = own ? [own.id, `type:${st.id}`] : [`type:${st.id}`];
                  return stepRow(st.id, keys, [...keys, st.id], st.operation_id, own?.note ?? st.note ?? null, value, (v) => typeStepQty(st, v), stepOf(own?.id ?? `type:${st.id}`), own, st.qty);
                })}
                {addedSteps.map((l) =>
                  stepRow(l.id, [l.id], [l.id], l.operation_id, l.note ?? 'added on this job', l.qty === null || l.qty === undefined ? '' : String(l.qty), (v) => updateRow('job_route_line', l.id, { qty: v === '' ? null : v }), stepOf(l.id), l, null, l),
                )}
              </tbody>
            </table>
          </div>
        )}
        {canEdit && (
          <div className="adds">
            <select value="" onChange={(e) => e.target.value && void insertRow('job_route_line', { job_id: jobId, type_step_id: null, operation_id: e.target.value, qty: null, sort: addedSteps.length + 1 })}>
              <option value="">+ Step of this job's own…</option>
              {opGroups.map((g) => (
                <optgroup key={g.key} label={g.label}>
                  {g.ops.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}
      </Grp>

      <Grp title="Job BOM" summary={`${bomCount} ${bomCount === 1 ? 'line' : 'lines'} · ${money(bomCents)}`} who={<span className="who">job-wide lines, not on an item</span>} open={folds.isOpen('bom')} onToggle={() => folds.toggle('bom')}>
        <div className="scr">
          <table className="kvt kv-rt">
            <colgroup>
              <col className="c-k" />
              <col className="c-std" />
              <col />
              <col className="c-i" />
            </colgroup>
            <thead>
              <tr>
                <th>Line</th>
                <th>Standard</th>
                <th>This job</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {typeLines.map((t) => {
                const own = ownLine(t);
                const value = own ? (own.qty === null || own.qty === undefined ? '' : String(own.qty)) : t.qty === null || t.qty === undefined ? '' : String(t.qty);
                const keys = own ? [own.id, `type:${t.id}`, t.id] : [`type:${t.id}`, t.id];
                return bomRow(t.id, keys, t.material_id, own?.note ?? t.note ?? null, value, (v) => typeLineQty(t, v), lineOf(own?.id ?? `type:${t.id}`), own, t.qty);
              })}
              {addedLines.map((l) =>
                bomRow(l.id, [l.id], l.material_id, l.note ?? 'added on this job', l.qty === null || l.qty === undefined ? '' : String(l.qty), (v) => updateRow('job_bom_line', l.id, { qty: v === '' ? null : v }), lineOf(l.id), l, null, l),
              )}
              <tr>
                <td className="k">Job size</td>
                <td className="cd">{smallUpTo !== null && Number.isFinite(smallUpTo) ? `small ≤ ${qty(smallUpTo)} LF` : 'no band set'}</td>
                <td className="ro">{sizeWords}</td>
                <td className="i">
                  <Info left>A job up to {smallUpTo ?? '—'} linear feet is a small job; the Job BOM's small/large-job line reads it. The line is a setting under Settings → Estimating (job size bands); the linear feet are the job's, above.</Info>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {canEdit && (
          <div className="adds">
            <select value="" onChange={(e) => e.target.value && void insertRow('job_bom_line', { job_id: jobId, type_line_id: null, material_id: e.target.value, qty: null, sort: addedLines.length + 1 })}>
              <option value="">+ Material of this job's own…</option>
              {mats.rows.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.description}
                </option>
              ))}
            </select>
          </div>
        )}
      </Grp>
    </>
  );
}

// ---------- rooms ----------

/**
 * The job's rooms, folded under the standards grid. Each follows the job's standards or holds its own values for some of
 * them; its items (and their children) read those. Sheets and setups pool by the job or by the room, as the pooling
 * setting says.
 */
function Rooms({ ctx, shown, defs, canEdit, pooling, open: isOpen, onToggle, onOpen }: { ctx: JobCtx; shown: LibStandardDef[]; defs: Row[]; canEdit: boolean; pooling: string; open: boolean; onToggle: () => void; onOpen: () => void }) {
  const { data, r, jobId } = ctx;
  const lib = r.lib!;
  const res = r.res!;
  const rooms = data.rooms;
  const folded = !isOpen;
  const [open, setOpen] = useState<string | null>(null);
  const itemsIn = (roomId: string | null) => res.items.filter((i) => !i.parent_item_id && (i.room_id ?? null) === roomId);
  const unplaced = itemsIn(null).length;

  const add = async () => {
    const name = window.prompt('Name of the room:', rooms.length === 0 ? 'Kitchen' : '');
    if (!name || !name.trim()) return;
    if (rooms.some((x) => x.name.toLowerCase() === name.trim().toLowerCase())) return window.alert(`This job already has a room called "${name.trim()}".`);
    const row = await insertRow('room', { job_id: jobId, name: name.trim(), sort: rooms.length + 1 });
    onOpen();
    setOpen(row.id);
  };
  const remove = async (room: Row) => {
    const n = itemsIn(room.id).length;
    if (!window.confirm(n > 0 ? `Remove the room "${room.name}"? Its ${n} ${n === 1 ? 'item stays' : 'items stay'} on the job, in no room, reading the job's standards.` : `Remove the room "${room.name}"?`)) return;
    await deleteRow('room', room.id);
  };

  return (
    <div className={`grp${folded ? ' folded' : ''}`}>
      <h4>
        <button type="button" className="foldh" aria-expanded={!folded} onClick={onToggle}>
          <span className={`chev${folded ? '' : ' open'}`} />
          Rooms
        </button>
        <span className="ct">{rooms.length === 0 ? 'none · every item follows the job' : `${rooms.length} · ${rooms.map((x) => x.name).join(', ')}`}</span>
        <span className="who">
          <Info left>
            A job may have several rooms: a kitchen and a pantry on one job. A room follows the job's standards, or holds its own value for any of them; its items, and the doors and drawer boxes they create, read the room's values. A Build's override still wins over a room. An item is put in a room on its Item tab, or when it is added. Sheets and setups pool across <b>{pooling === 'room' ? 'each room' : 'the whole job'}</b> (Settings → Estimating → Pooling).
          </Info>
        </span>
      </h4>
      {!folded && (
        <>
          {canEdit && (
            <div className="adds">
              <Btn small onClick={() => void add()}>
                + Room
              </Btn>
            </div>
          )}
          {rooms.length === 0 && <Empty>No rooms: every item reads the job's standards. Add rooms when one job covers more than one.</Empty>}
          {rooms.map((room) => {
            const n = itemsIn(room.id).length;
            const own = !!room.own_standards;
            const isOpen = open === room.id;
            return (
              <div key={room.id} className="ln" style={{ display: 'block' }}>
                <div className="row mid" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <b>
                    <Edit className="cell" disabled={!canEdit} value={room.name} parse={(t) => (t.trim() ? t.trim() : undefined)} onSave={(v) => updateRow('room', room.id, { name: v })} />
                  </b>
                  <small>
                    {n} {n === 1 ? 'item' : 'items'}
                  </small>
                  <span className="grow" />
                  <select disabled={!canEdit} value={own ? 'own' : 'job'} onChange={(e) => void updateRow('room', room.id, { own_standards: e.target.value === 'own' })} title="Whether this room reads the job's standards or holds its own">
                    <option value="job">Follows the job's standards</option>
                    <option value="own">Has its own standards</option>
                  </select>
                  {own && (
                    <Btn small read onClick={() => setOpen(isOpen ? null : room.id)}>
                      {isOpen ? 'Hide' : 'Set'} its standards
                    </Btn>
                  )}
                  {canEdit && (
                    <>
                      <Move table="room" rows={rooms} id={room.id} />
                      <Btn small onClick={() => void remove(room)}>
                        ×
                      </Btn>
                    </>
                  )}
                </div>
                {own && isOpen && <RoomStandards ctx={ctx} room={room} shown={shown} defs={defs} canEdit={canEdit} />}
                {own && !isOpen && (
                  <small>
                    {(() => {
                      const held = data.roomStandards.filter((x) => x.room_id === room.id);
                      const names = held.map((x) => lib.standards.find((d) => d.key === defs.find((dd) => dd.id === x.standard_def_id)?.key)?.label).filter(Boolean);
                      return names.length ? `Its own: ${names.join(', ')}. Everything else follows the job.` : 'Holds no value of its own yet: everything follows the job until one is set.';
                    })()}
                  </small>
                )}
              </div>
            );
          })}
          {rooms.length > 0 && unplaced > 0 && (
            <small>
              {unplaced} {unplaced === 1 ? 'item is' : 'items are'} in no room and read the job's standards: {itemsIn(null).map((i) => i.number).join(', ')}.
            </small>
          )}
        </>
      )}
    </div>
  );
}

/** The standards one room holds for itself: each line shows the job's value until the room sets its own. */
function RoomStandards({ ctx, room, shown, defs, canEdit }: { ctx: JobCtx; room: Row; shown: LibStandardDef[]; defs: Row[]; canEdit: boolean }) {
  const { data, r } = ctx;
  const lib = r.lib!;
  const res = (ctx.asEntered.res ?? r.res)!;
  const held = data.roomStandards.filter((x) => x.room_id === room.id);
  const set = async (def: LibStandardDef, value: Scalar) => {
    const row = defs.find((d) => d.key === def.key);
    if (!row) return;
    const existing = held.find((x) => x.standard_def_id === row.id);
    if (value === null || value === '') {
      if (existing) await deleteRow('room_standard', existing.id);
    } else {
      await upsertRow('room_standard', { room_id: room.id, standard_def_id: row.id, value: String(value) }, 'room_id,standard_def_id');
    }
  };
  // the standards in effect in this room: its own values over the job's
  const inRoom: Record<string, Scalar> = { ...res.standards };
  for (const x of held) {
    const key = defs.find((d) => d.id === x.standard_def_id)?.key;
    if (key) inRoom[key] = x.value as Scalar;
  }
  return (
    <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: '3px solid var(--line)' }}>
      <small>Each line reads the job's value until this room sets its own. Reset puts a line back to the job's.</small>
      {shown.map((def) => {
        const row = defs.find((d) => d.key === def.key);
        const mine = row ? held.find((x) => x.standard_def_id === row.id) : undefined;
        const jobValue = res.standards[def.key] ?? null;
        const value = mine ? (def.data_type === 'boolean' ? /^(true|yes|1)$/i.test(String(mine.value)) : (mine.value as Scalar)) : jobValue;
        return (
          <StandardLine
            key={def.key}
            def={def}
            row={row}
            lib={lib}
            value={value}
            changed={!!mine}
            changedWord="this room's"
            canEdit={canEdit}
            onSet={(v) => void set(def, v)}
            standards={inRoom}
            extra={
              <div>
                <b>The job's value:</b> {words(lib, def, jobValue)}
              </div>
            }
          />
        );
      })}
    </div>
  );
}
